import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Table,
  Upload,
  message,
  Space,
  Row,
  Col,
  Modal,
  Popover } from
"antd";
import type { UploadProps, TableColumnsType } from "antd";
import * as XLSX from "xlsx";
import moment from "moment";
import { getUuid } from "@src/helpers/functions";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  createSubjectListIterativeClear,
  createSubjectListIterativeRequest,
  createSubjectListIterativeSelector,
  CreateSubjectListInput,
  getMyOrganization,
  getMyOrganizationRequest } from
"@src/redux";
import BulkUploadFilter from "../components/filters/bulk-upload-filter/BulkUploadFilter";
import BulkUploadIterativeList from "../components/filters/bulk-upload-filter/BulkUploadIterativeList";
import { useTranslation } from "react-i18next";
import { excelFormatUrl } from "@src/helpers/constants";

interface BulkRow {
  key: string;
  sNo: string;
  referenceId: string;
  name: string;
  mobile: string;
  country_code: string;
  email: string;
  gender: string;
  bloodGroup: string;
  dob: string;
  address: string;
  smoker: string;
  diabetic: string;
  hypertensive: string;
  status: string;
  type: string;
  updateReferenceId?: string;
  height?: string;
  weight?: string;
}

const columns: TableColumnsType<BulkRow> = [
{ title: "S.No", dataIndex: "s_no", key: "s_no", width: 70, fixed: "left" },
{ title: "Name", dataIndex: "name", key: "name", ellipsis: true },
{
  title: "Ref ID",
  dataIndex: "referenceId",
  key: "referenceId",
  ellipsis: true
},
{ title: "Mobile", dataIndex: "mobile", key: "mobile", ellipsis: true },
{ title: "Email", dataIndex: "email", key: "email", ellipsis: true },
{ title: "Gender", dataIndex: "gender", key: "gender", width: 80 },
{
  title: "Blood Group",
  dataIndex: "bloodGroup",
  key: "bloodGroup",
  width: 100
},
{
  title: "DOB",
  dataIndex: "dob",
  key: "dob",
  width: 100,
  render: (dob: string) => dob ? moment(dob).format("DD-MM-YYYY") : "-"
},
{ title: "Address", dataIndex: "address", key: "address", ellipsis: true },
{ title: "Smoker", dataIndex: "smoker", key: "smoker", width: 80 },
{ title: "Diabetic", dataIndex: "diabetic", key: "diabetic", width: 90 },
{
  title: "Hypertensive",
  dataIndex: "hypertensive",
  key: "hypertensive",
  width: 120
},
{
  title: "Status",
  dataIndex: "status",
  key: "status",
  width: 120,
  fixed: "right"
}];

const BulkSubjectUpload: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string>("");
  const [tableData, setTableData] = useState<BulkRow[]>([]);
  const [lastOrgId, setLastOrgId] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { result: subjectListIterative } = useSelector(
    createSubjectListIterativeSelector
  );

  const { result: myOrganization } = useSelector(getMyOrganization);

  // useEffect(() => {
  //   dispatch(getMyOrganizationRequest({}));
  // }, []);
  useEffect(() => {
    if (subjectListIterative?.items?.length) setModalOpen(true);
  }, [subjectListIterative]);

  const parseExcelDate = (value: string | number): string | null => {
    if (!value) return null;
    if (typeof value === "number") {
      const date = XLSX.SSF.parseDateCode(value);
      return new Date(date.y, date.m - 1, date.d).toISOString();
    }
    if (typeof value === "string") {
      const [dd, mm, yyyy] = value.split("-").map(Number);
      if (dd && mm && yyyy) return new Date(yyyy, mm - 1, dd).toISOString();
    }
    return null;
  };

  const parseExcelBuffer = (buffer: ArrayBuffer) => {
    const msgKey = "bulk_upload_parse";
    message.loading({
      content: "Parsing Excel file...",
      key: msgKey,
      duration: 0
    });
    try {
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawRows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!rawRows.length) {
        message.error({ content: "Excel file is empty", key: msgKey });
        return;
      }
      const filteredRows = rawRows.filter((r) =>
      Object.values(r).some((v) => String(v).trim() !== "")
      );
      const formatted: BulkRow[] = filteredRows.map((row, i) => {
        const safeYn = (val: any) =>
        val === undefined || val === null || String(val).trim() === "" ?
        "-" :
        String(val).toUpperCase() === "Y" ?
        "Y" :
        "N";

        return {
          key: i.toString(),
          sNo: row["S.No"] ?? String(i + 1),
          referenceId: row["ReferenceId"] ?? "",
          name: row["Name"] ?? "",
          mobile: row["Mobile"] ?? "",
          country_code: row["CountryCode"] ?? "+91",
          email: row["Email"] ?? "",
          gender: row["Gender"] ?? "",
          bloodGroup: row["BloodGroup"] ?? row["Blood Group"] ?? "",
          dob: parseExcelDate(row["DOB"]) ?? "",
          address: row["Address"] ?? "",
          smoker: safeYn(row["Smoker"]),
          diabetic: safeYn(row["Diabetic"]),
          hypertensive: safeYn(row["Hypertensive"]),
          status: row["Status"] ?? "-",
          type: row["Type"] ?? ""
        };
      });
      setTableData(formatted);
      message.success({
        content: `Loaded ${formatted.length} row(s)`,
        key: msgKey
      });
    } catch {
      message.error({ content: "Invalid Excel file", key: msgKey });
    }
  };

  const handleFileChange: UploadProps["onChange"] = (info) => {
    const file = info.file.originFileObj as File;
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => parseExcelBuffer(e.target?.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  };

  const formatBloodGroup = (bg: string) =>
  bg.replace("+", "_positive").replace("-", "_negative").toLowerCase();

  const organizationSubjectKey =
  myOrganization?.rootOrganization?.subjectKeyList?.find(
    (i) => i.isMandatory
  ) || myOrganization?.rootOrganization?.subjectKeyList?.[0];

  const buildPayload = (replace: boolean): CreateSubjectListInput =>
    ({
      canReplace: replace,
      organizationId: lastOrgId || "",

      subjects:
        tableData?.map((row) => {
          const id = getUuid();
          return {
            id,
            referenceId: row.referenceId,
            name: row.name,
            mobile: JSON.stringify({
              number: row.mobile,
              countryCode: row.country_code?.toString().toLowerCase() || "",
              countryShortCode: row.country_code || ""
            }),
            email: row.email,
            address: row.address,
            gender: row.gender.toLowerCase(),
            bloodGroup: formatBloodGroup(row.bloodGroup),
            dob: row.dob || "",
            subjectMetric: {
              weight: row.weight || "",
              height: row.height || "",
              isSmoker: row.smoker === "Y",
              isDiabetic: row.diabetic === "Y",
              isHypertensive: row.hypertensive === "Y"
            },
            keyValueList: [
              {
                id: id + "_" + organizationSubjectKey?.id,
                subjectId: id,
                organizationSubjectKeyId: organizationSubjectKey?.id,
                value: row.referenceId
              }
            ],

            subjectType:
              row.type === "General"
                ? "general"
                : row.type === "ANC"
                  ? "anc"
                  : "",
            updateReferenceId: getUuid()
          };
        }) ?? []
    }) as CreateSubjectListInput;

  const handleUpload = (replace: boolean) => {
    form.validateFields();
    if (!tableData.length) return message.warning("No data to upload");
    const payload = buildPayload(replace);
    dispatch(createSubjectListIterativeRequest({ data: payload }));
  };
  const handleClear = () => {
    setTableData([]);
    form.resetFields(["excel_url"]);
  };

  const onCancel = () => {
    setModalOpen(false);
    setLastOrgId("");
    dispatch(createSubjectListIterativeClear());
  };

  const handleDownloadExcelFormat = () => {
    const link = document.createElement("a");
    link.href = "/subject_upload.xlsx";
    link.download = "subject_upload.xlsx";
    link.click();
  };

  return (
    <div className="bulk-upload-container">
      <Form form={form} layout="vertical">
        <Row
          gutter={24}
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "10px",
            backgroundColor: "#ebebeb",
            padding: "10px"
          }}>
          
          <Col>
            <span style={{ fontSize: "20px", fontWeight: "bold" }}>
              {t("bulk_upload")}
            </span>
          </Col>
          <Col>
            <div className="bulk-upload-header-actions">
              <Form.Item
                name="excel_url"
                rules={[
                { required: true, message: "Please upload an Excel file" }]}

                valuePropName="fileList"
                getValueFromEvent={(e) =>
                Array.isArray(e) ? e : e && e.fileList}

                style={{ marginBottom: 0 }}>
                
                <Upload
                  accept=".xls,.xlsx"
                  showUploadList={false}
                  maxCount={1}
                  onChange={handleFileChange}>
                  
                  <Popover
                    content={
                    myOrganization?.rootOrganization?.subjectKeyList &&
                    myOrganization?.rootOrganization?.subjectKeyList?.
                    length > 0 ?
                    t("bulk_upload") :
                    t("no_subject_keys_available")}>

                    
                    <Button
                      type="primary"
                      disabled={
                      myOrganization?.rootOrganization?.subjectKeyList &&
                      myOrganization?.rootOrganization?.subjectKeyList?.
                      length > 0 ?
                      false :
                      true}>

                      
                      {t("choose_excel_file")}
                    </Button>
                  </Popover>
                </Upload>
              </Form.Item>
              <span
                onClick={handleDownloadExcelFormat}
                className="download-format-link">
                
                {t("download_format")}
              </span>
            </div>
            <span>{fileName}</span>
          </Col>
        </Row>
        <BulkUploadFilter form={form} setLastOrgId={setLastOrgId} />
      </Form>

      <div className="bulk-upload-table-container">
        <Table
          columns={columns}
          dataSource={tableData}
          bordered
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content", y: 400 }}
          sticky />
        
      </div>

      {tableData.length > 0 &&
      <Row
        style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        
          <Space wrap>
            <Button danger onClick={handleClear}>
              {t("clear")}
            </Button>
            <Button type="primary" onClick={() => handleUpload(false)}>
              {t("upload")}
            </Button>
            <Button type="primary" onClick={() => handleUpload(true)}>
              {t("upload_replace")}
            </Button>
          </Space>
        </Row>}
      

      <Modal
        title={t("view_upload_list")}
        footer={[
        <Button key="close" onClick={onCancel}>
            {t("close")}
          </Button>]}

        open={modalOpen}
        onCancel={onCancel}
        width={600}>
        
        <BulkUploadIterativeList
          subjectListIterative={subjectListIterative} />
        
      </Modal>
    </div>);

};
export default BulkSubjectUpload;